import {
  Box,
  Checkbox,
  CircularProgress,
  TableCell,
  TableRow
} from "@material-ui/core";
import { ClassNameMap } from "@material-ui/styles/withStyles";
import RemittanceManualAdjustmentFlag from "@saleor/remittanceRunner/components/RemittanceManualAdjustmentFlag";
import RemittanceRunnerCheckbox from "@saleor/remittanceRunner/components/RemittanceRunnerCheckbox/RemittanceRunnerCheckbox";
import RemittanceRunnerRemittanceIsLoading from "@saleor/remittanceRunner/components/RemittanceRunnerRemittanceIsLoading";
import { RemittanceRunnerOrders_remittanceOrders_edges_node } from "@saleor/remittanceRunner/types/RemittanceRunnerOrders";
import { remittanceRunnerPath } from "@saleor/remittanceRunner/url";
import { RemittanceMotherOrder } from "@saleor/remittanceRunner/utils/formDataTypes";
import {
  RemittanceManualAdjustmentStatusEnum,
  RemittanceStatusEnum,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import React from "react";

import {
  RemittanceRunnerTblColAging,
  RemittanceRunnerTblColCompanyName,
  RemittanceRunnerTblColDataDate,
  RemittanceRunnerTblColDataText,
  RemittanceRunnerTblColDispatcher,
  RemittanceRunnerTblColMall,
  RemittanceRunnerTblColMotherOrder,
  RemittanceRunnerTblColPaymentTypeGrossAmount,
  RemittanceRunnerTblColStatus,
  RemittanceRunnerTblColYesNo
} from "../RemittanceRunnerTableColumns";

interface TableBodyContentProp {
  tblClasses: ClassNameMap<string>;
  numberOfColumns: number;
  dateFormat: string;
  isOmiAdmin: boolean;
  isSuperUser: boolean;
  userCanOverrideRemittance: boolean;
  loadingOrders: boolean;
  remittancesList: RemittanceRunnerOrders_remittanceOrders_edges_node[];
  remittanceIdList: string[];
  setMotherOrderList: React.Dispatch<React.SetStateAction<string[]>>;
  setMotherOrderCode: React.Dispatch<
    React.SetStateAction<RemittanceMotherOrder>
  >;
  onRefetchOrders: () => void;
}

export const TableBodyContent: React.FC<TableBodyContentProp> = props => {
  const {
    tblClasses,
    numberOfColumns,
    dateFormat,
    isOmiAdmin,
    isSuperUser,
    userCanOverrideRemittance,
    loadingOrders,
    remittancesList,
    remittanceIdList,
    setMotherOrderList,
    setMotherOrderCode,
    onRefetchOrders
  } = props;

  const updateRemittanceList = (
    prevList: string[],
    remittanceId: number,
    isChecked: boolean
  ): string[] => {
    const remittanceIdStr = String(remittanceId);

    return isChecked
      ? [...prevList, remittanceIdStr]
      : prevList.filter(id => id !== remittanceIdStr);
  };

  const handleRemittanceSelected = (
    remittance,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;

    setMotherOrderList(prevList =>
      updateRemittanceList(prevList, remittance?.id, isChecked)
    );

    if (isChecked) {
      setMotherOrderCode(prev => ({
        ...prev,
        orderCode: remittance?.orderCode
      }));
    } else {
      setMotherOrderCode(prev => ({
        ...prev,
        orderCode: ""
      }));
    }
  };

  const validForBulkOverride = remittancesList.filter(
    remittance =>
      (remittance?.remittanceLine === null ||
        (remittance?.remittanceLine !== null &&
          remittance?.remittanceLine?.remittance?.status ===
            RemittanceStatusEnum.TO_REMIT)) &&
      remittance?.remittanceManualAdjustmentStatus !==
        RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT &&
      remittance?.smopStatus === SmopOrderStatusEnum.COMPLETED
  );

  const getTableBodyContentToRender = () => {
    let contentToRender;

    if (loadingOrders) {
      contentToRender = (
        <TableRow>
          <TableCell colSpan={numberOfColumns}>
            <RemittanceRunnerRemittanceIsLoading
              className={tblClasses.toRemitTableForLoading}
            />
          </TableCell>
        </TableRow>
      );
    } else if (!loadingOrders && remittancesList.length === 0) {
      contentToRender = (
        <TableRow>
          <TableCell colSpan={numberOfColumns}>
            {"No mother orders found"}
          </TableCell>
        </TableRow>
      );
    } else {
      contentToRender = (
        <>
          {remittancesList.map(remittance => {
            const isForConfirmation =
              remittance?.remittanceLine?.remittance?.status ===
              RemittanceStatusEnum.FOR_BANK_CONFIRMATION;

            const isDisabled =
              remittance?.remittanceLine?.remittance?.status ===
                RemittanceStatusEnum.REMITTED ||
              remittance?.remittanceManualAdjustmentStatus ===
                RemittanceManualAdjustmentStatusEnum.FOR_ADJUSTMENT ||
              remittance?.smopStatus !== SmopOrderStatusEnum.COMPLETED ||
              !userCanOverrideRemittance;
            return (
              <TableRow
                key={remittance.id}
                onClick={() => {
                  if (
                    remittance?.remittanceLine?.remittance?.remittanceNo &&
                    isOmiAdmin
                  ) {
                    window.open(
                      remittanceRunnerPath(
                        remittance?.remittanceLine?.remittance?.id
                      )
                    );
                  }
                }}
              >
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  {!isForConfirmation ? (
                    <Checkbox
                      className={tblClasses.remittanceCheckbox}
                      disabled={isDisabled || validForBulkOverride.length === 0}
                      icon={<RemittanceRunnerCheckbox />}
                      checked={
                        remittanceIdList.includes(remittance.id) &&
                        validForBulkOverride.length > 0
                      }
                      onChange={event =>
                        handleRemittanceSelected(remittance, event)
                      }
                    />
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <CircularProgress size={20} />
                    </Box>
                  )}
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                  key={remittance.orderCode}
                >
                  <div className={tblClasses.divFlex}>
                    <RemittanceManualAdjustmentFlag
                      remittanceOrder={remittance}
                      isRemittanceOrder={true}
                      onRefetchOrders={onRefetchOrders}
                    />
                    <RemittanceRunnerTblColMotherOrder
                      remittance={remittance}
                      isOmiAdmin={isOmiAdmin}
                    />
                  </div>
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColStatus
                    status={remittance?.remittanceLine?.remittance?.status}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColDataDate
                    dataTitle={"Remittance Date"}
                    dataValue={
                      remittance?.remittanceLine?.remittance?.remittedAt
                    }
                    dateFormat={dateFormat}
                    centeredText={true}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColDispatcher
                    remitter={remittance?.remittanceLine?.remittance?.remitter}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColCompanyName
                    dataTitle={"Company Name"}
                    isRider3pl={remittance?.isRider3pl}
                    courier={remittance?.courier}
                    companyName={remittance?.rider?.homeAddress?.companyName}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColPaymentTypeGrossAmount
                    paymentMethodType={
                      remittance?.payments[0]?.paymentMethodType
                    }
                    amount={remittance?.total?.gross?.amount}
                    remittanceOrder={remittance}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColAging
                    remittance={remittance?.remittanceLine?.remittance}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColDataText
                    dataTitle={"Remittance ID"}
                    dataValue={
                      remittance?.remittanceLine?.remittance?.remittanceNo
                    }
                    isClickable={true}
                    onClick={() => {
                      if (
                        remittance?.remittanceLine?.remittance?.remittanceNo &&
                        !isOmiAdmin
                      ) {
                        window.open(
                          remittanceRunnerPath(
                            remittance?.remittanceLine?.remittance?.id
                          )
                        );
                      }
                    }}
                  />
                </TableCell>
                {(isOmiAdmin || isSuperUser) && (
                  <TableCell
                    className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                  >
                    <RemittanceRunnerTblColMall mall={remittance?.mall?.name} />
                  </TableCell>
                )}
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColYesNo
                    label={"Payment Collection"}
                    value={remittance?.isPaymentCollected}
                  />
                </TableCell>
                <TableCell
                  className={`${tblClasses.tableCellBody} ${tblClasses.colStatus}`}
                >
                  <RemittanceRunnerTblColYesNo
                    label={"Receipt Uploaded"}
                    value={
                      !!remittance?.remittanceLine?.remittance?.receiptImage
                        ?.url
                    }
                    blank={!remittance?.remittanceLine?.remittance?.id}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </>
      );
    }

    return contentToRender;
  };

  return getTableBodyContentToRender();
};

export default TableBodyContent;
